<script lang="ts">
  import Swal from 'sweetalert2';
  import {STEP} from '@/types/common';
  import {step, project, recap} from '@/store';
  import disabled from '@/store/disabled';

  import VideoPlayer from './VideoPlayer.svelte';

  import {Container} from '@components';

  import {topnav} from '@/utils/top-nav-next-custom-events';
  import {onDestroy, onMount} from 'svelte';
  import Subtitle from './Subtitle.svelte';
  import {RecapProject, Timestamp, TimestampText} from '@/store/recap';
  import {RecapService} from '@/services';
  import AiSuggestedIntro from './AiSuggestedIntro.svelte';

  $: $disabled = $project.selectedSentences.length === 0;

  let rawRecap: RecapProject;
  let recap_id = -1;
  let videoSrc = '';
  let timestamps: Timestamp[] = [];
  let selectedTranscriptions: TimestampText[] = [];

  const recapService = new RecapService();

  const updateNextStep = async () => {
    if (timestamps.length <= 0) {
      openSwal();
      return;
    }

    try {
      // save project then generate AI suggested intro

      const intro_timestamps = selectedTranscriptions
        .filter(text => text.isHighlighted === true)
        .map(text => ({startDuration: text.timeStart, endDuration: text.timeEnd})) as Timestamp[];

      const updatedRecapData = await recapService.update(recap_id, {
        step: STEP.RECAP_PREVIEW,
        intro_timestamps,
        transcription: rawRecap.transcription?.map(value => {
          value.isFollowingText = false;
          value.isSearchWord = false;
          return value;
        })
      });

      console.log({updatedRecapData});

      recap.update(value => {
        if (!updatedRecapData.intro_timestamps) return value;
        console.log('SUGGEST INTRO UPDATE');
        value.intro_timestamps = updatedRecapData.intro_timestamps;
        if (!updatedRecapData.composition) return value;
        console.log('COMPOSITION UPDATE');
        value.composition = updatedRecapData.composition;
        step.update(STEP.RECAP_PREVIEW);
        return value;
      });
    } catch (error) {
      Swal.fire({
        text: 'Something went wrong saving your sermon recap. Please try again',
        icon: 'error',
        titleText: 'Failed to save Sermon Recap'
      });
    }
  };

  const updatePrevStep = async () => {
    step.update(STEP.HIGHLIGHT_SUBTITLE);
    recapService.update(recap_id, {
      step: STEP.HIGHLIGHT_SUBTITLE
    });
  };

  const openSwal = () => {
    Swal.fire({
      text: 'Please select atleast one (1) subtitle to continue.',
      icon: 'warning',
      titleText: 'Warning No Subtitle Selected'
    });
  };

  const recapSubscription = recap.subscribe(value => {
    rawRecap = value;
    recap_id = value.id;
    if (value.video_src) videoSrc = value.video_src;
    timestamps = value.intro_timestamps || [];

    // run once since highlighted subtitles from step 1 won't update anyway and if I don't have this condition it will reset the isHighlighted for the selectedIntro bit
    if (selectedTranscriptions.length > 0) return;
    const transcription = value.transcription || [];

    selectedTranscriptions = [];
    for (let i = 0; i < transcription.length; i++) {
      const word = transcription[i];
      if (!word.isHighlighted) continue;
      selectedTranscriptions.push({
        index: i,
        timeStart: word.timeStart,
        timeEnd: word.timeEnd,
        text: word.text,
        isHighlighted: false,
        isFollowingText: false,
        isSearchWord: false
      });
    }
  });

  const updateTimestamp = (event: any) => {
    recap.update(value => {
      value.intro_timestamps = event.detail.timestamps || [];
      return value;
    });
  };

  const onTimeUpdate = (e: {detail: {videoTime: number; currentTime: number}}) => {
    const event = e.detail;
    for (let i = 0; i < timestamps.length; i++) {
      const timestamp = timestamps[i];

      if (event.videoTime >= timestamp.startDuration && event.videoTime <= timestamp.endDuration) {
        for (let j = timestamp.startIndex; j <= timestamp.endIndex; j++) {
          const transcription = selectedTranscriptions[j];
          transcription.isFollowingText = false;
          if (event.videoTime >= transcription.timeStart && event.videoTime <= transcription.timeEnd) {
            transcription.isFollowingText = true;
          }
        }
        continue;
      }

      for (let j = timestamp.startIndex; j <= timestamp.endIndex; j++) {
        selectedTranscriptions[j].isFollowingText = false;
      }
    }

    selectedTranscriptions = selectedTranscriptions;
  };

  function highlightFromSavedData() {
    for (let i = 0; i < selectedTranscriptions.length; i++) {
      const transcription = selectedTranscriptions[i];

      transcription.isHighlighted = false;

      for (let j = 0; j < timestamps.length; j++) {
        const timestamp = timestamps[j];
        if (transcription.timeStart >= timestamp.startDuration && transcription.timeEnd <= timestamp.endDuration) {
          transcription.isHighlighted = true;
        }
      }
    }

    selectedTranscriptions = selectedTranscriptions;
  }

  function onSelectIntro(e: {detail: {startDuration: number; endDuration: number}}) {
    const event = e.detail;

    timestamps = [];

    const indexes = [];

    for (let i = 0; i < selectedTranscriptions.length; i++) {
      const transcription = selectedTranscriptions[i];

      transcription.isHighlighted = false;

      if (transcription.timeStart >= event.startDuration && transcription.timeEnd <= event.endDuration) {
        transcription.isHighlighted = true;
        indexes.push(i);
      }
    }

    timestamps.push({
      startDuration: event.startDuration,
      endDuration: event.endDuration,
      startIndex: indexes[0],
      endIndex: indexes[indexes.length - 1]
    });

    selectedTranscriptions = selectedTranscriptions;
  }

  onMount(() => {
    topnav.disabledNext(openSwal);
    topnav.next(updateNextStep);
    topnav.previous(updatePrevStep);

    highlightFromSavedData();
  });

  onDestroy(() => {
    recapSubscription();
  });
</script>

<Container className="flex">
  <AiSuggestedIntro on:selected-intro={onSelectIntro} {selectedTranscriptions} />
  <Subtitle transcriptions={selectedTranscriptions} on:timestamp-change={updateTimestamp} />
  <div class="relative flex h-full flex-grow items-center overflow-auto">
    <VideoPlayer {videoSrc} {timestamps} on:timeupdate={onTimeUpdate} />
  </div>
</Container>
